import axios from 'axios'

import { clearPartnerSession, getPartnerToken } from '../lib/utils/partner-session'
import { isTokenExpired } from '../utils/helpers'
import { PARTNER_INVESTORS, PARTNER_LOGIN, PARTNER_LOGOUT, PARTNER_SET_PASSWORD } from './endpoints'
import {
  PartnerApiError,
  PartnerInvestorsResponse,
  PartnerLoginResponse,
  PartnerSetPasswordResponse,
} from './partner.types'

const partnerAxios = axios.create()

const isLoginRequest = (url?: string) => !!url && url.includes('/v1/partner/auth/login')

const redirectToPartnerLogin = () => {
  if (window.location.pathname !== '/partner') {
    window.location.href = '/partner'
  }
}

partnerAxios.interceptors.request.use(
  (config) => {
    if (isLoginRequest(config.url)) return config

    const token = getPartnerToken()
    if (!token) return config

    let expired = false
    try {
      expired = isTokenExpired(token)
    } catch {
      expired = false
    }

    if (expired) {
      clearPartnerSession()
      redirectToPartnerLogin()
      return Promise.reject(new Error('Partner session expired'))
    }

    config.headers = { ...config.headers, Authorization: token }
    return config
  },
  (error) => Promise.reject(error),
)

partnerAxios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status
    const url = error?.config?.url as string | undefined
    if (status === 401 && !isLoginRequest(url)) {
      clearPartnerSession()
      redirectToPartnerLogin()
    }
    return Promise.reject(error)
  },
)

export const PARTNER_INVESTOR_PAGE_SIZE = 10

const readResponse = (error: unknown) => {
  if (!error || typeof error !== 'object') return null
  const candidate = error as {
    isAxiosError?: boolean
    response?: { status?: number; data?: unknown }
  }
  if (!axios.isAxiosError(error) && candidate.isAxiosError !== true) return null
  return candidate.response ?? null
}

export const getPartnerApiError = (error: unknown, fallback: string): PartnerApiError => {
  const response = readResponse(error)
  if (response) {
    const data = response.data
    let code: string | undefined
    let message = fallback
    if (typeof data === 'string' && data.trim()) {
      message = data.trim()
    } else if (data && typeof data === 'object') {
      const rawCode = (data as { code?: unknown }).code
      const rawMessage = (data as { message?: unknown }).message
      if (typeof rawCode === 'string' && rawCode.trim()) code = rawCode
      if (typeof rawMessage === 'string' && rawMessage.trim()) message = rawMessage.trim()
    }
    return { code, status: response.status, message }
  }

  if (error instanceof Error && error.message.trim()) return { message: error.message }
  return { message: fallback }
}

export const getPartnerErrorMessage = (error: unknown, fallback: string) =>
  getPartnerApiError(error, fallback).message

export const partnerServices = {
  login: async (email: string, password: string, recaptchaToken?: string) => {
    const headers: Record<string, string> = {}
    if (recaptchaToken) headers['g-recaptcha-response'] = recaptchaToken

    const { data } = await partnerAxios.post<PartnerLoginResponse>(
      PARTNER_LOGIN,
      { email: email.trim().toLowerCase(), password },
      { headers },
    )
    if (!data?.authToken) {
      throw new Error('Login response did not include a token')
    }
    return data
  },

  setPassword: async (newPassword: string) => {
    const { data } = await partnerAxios.post<PartnerSetPasswordResponse>(PARTNER_SET_PASSWORD, {
      newPassword,
    })
    return data
  },

  logout: async () => {
    await partnerAxios.post(PARTNER_LOGOUT)
  },

  getInvestors: async (page: number, limit = PARTNER_INVESTOR_PAGE_SIZE) => {
    const safePage = Number.isFinite(page) && page > 0 ? Math.floor(page) : 1
    const safeLimit = Math.min(Math.max(Math.floor(limit) || PARTNER_INVESTOR_PAGE_SIZE, 1), 25)
    const { data } = await partnerAxios.get<PartnerInvestorsResponse>(PARTNER_INVESTORS, {
      params: { page: safePage, limit: safeLimit },
    })
    const investors = Array.isArray(data?.investors) ? data.investors : []
    return {
      page: data?.page ?? safePage,
      limit: data?.limit ?? safeLimit,
      total: data?.total ?? 0,
      investors: investors.map((investor) => ({
        ...investor,
        investments: Array.isArray(investor?.investments) ? investor.investments : [],
        redemptions: Array.isArray(investor?.redemptions) ? investor.redemptions : [],
        interests: Array.isArray(investor?.interests) ? investor.interests : [],
        payouts: Array.isArray(investor?.payouts) ? investor.payouts : [],
        interestTerms: Array.isArray(investor?.interestTerms) ? investor.interestTerms : [],
        wallet: investor?.wallet
          ? {
              ...investor.wallet,
              movements: Array.isArray(investor.wallet.movements) ? investor.wallet.movements : [],
            }
          : null,
      })),
    }
  },
}
